import React from 'react';
import {useHistory, Link} from 'react-router-dom';
import {Alert, Button} from 'reactstrap';
import Autosuggest from 'react-autosuggest';

import withAdvancedSearchInput from 'modules/advanced-search/withAdvancedSearchInput';
import useGetSearchInputLimit from 'modules/advanced-search/useGetSearchInputLimit';
import {UNABLE_TO_FIND_IMAGE_MATCHED} from 'configs';
import {ImageFlags} from 'components/Images';
import {useMembership} from 'contexts/MembershipContext';
import {
  SUBSCRIPTION_BILLING_METHOD,
  SUBSCRIPTION_TYPE_LEVEL,
} from 'configs/membership';
import {getCountryCodeById} from 'utils/locales';
import {getFormattedNumber} from 'utils/numbers';
import cx from 'classnames';
import styles from './AdvancedSearchInput.module.scss';
import {isEmpty, isEqual} from 'lodash';
import urlJoin from 'url-join';
import {MDB_PREFIX} from 'configs';
import {removePrefix} from 'utils/string';
import {useViewport} from 'contexts/ViewportContext';
import {VIEWPORT_WIDTHS} from 'configs';
import {useLimits} from 'contexts/LimitsContext';
import {alertByInsuffCredits} from 'utils/alerts';
import LocalStorage from 'utils/localStorage';
import MuiIcon from 'components/Icons/MuiIcon';

const SEARCH_TYPE = {
  ce: 'ce',
  cert: 'cert',
};

function AdvancedSearchInputV2(props) {
  const storedOutOfCredits = LocalStorage.showOutOfCredits;

  const {stateProps, suggestionProps, header, ...rest} = props;
  const history = useHistory();

  const {size} = useViewport();
  const isMobile = VIEWPORT_WIDTHS[size] < VIEWPORT_WIDTHS['sm'];

  const {
    limit,
    getLimit,
    isLoading: isLimitLoading,
  } = useGetSearchInputLimit({
    type: SEARCH_TYPE['ce'],
  });
  const {
    isSubscribed,
    subscriptionLevel,
    isTrial,
    billingMethod,
    isLegacyUser,
  } = useMembership();
  const {isLimitReached, toggleOutOfCreditsModal} = useLimits();

  const {
    // searchType,
    keyword,
    suggestions,
    isLoading,
    hasSearched,
    isFocused,
    // setKeyword,
    setHasSearched,
    setIsFocused,
    // getSuggestion,
    clearSuggestions,
    // clearAllStates,
    recordsCount,
  } = stateProps;
  const {
    onSuggestionsFetchRequested,
    onSuggestionsClearRequested,
    // handleSuggestionSelected,
    getSuggestionValue,
    themeGenerator,
    renderInputGenerator,
    inputProps,
  } = suggestionProps;

  const {try: limitTry, max: limitMax} = limit || {};
  const limitReached = limitTry === limitMax;

  const isBasic =
    subscriptionLevel === SUBSCRIPTION_TYPE_LEVEL['basic'] && !isTrial;
  const isStandard = subscriptionLevel === SUBSCRIPTION_TYPE_LEVEL['standard'];
  const isPaid = isBasic || isStandard;
  const isManualMethod = billingMethod === SUBSCRIPTION_BILLING_METHOD.manual;

  // first condition in this is for basic or standard
  // second condition is for free trial user
  const overallFocused =
    isPaid && isLegacyUser ? isFocused : keyword && isFocused;
  const overallLoading = isLimitLoading || isLoading;
  const displayLimit = isPaid && isLegacyUser;

  function renderSuggestion(suggestion) {
    const {company_name, countries_id} = suggestion;

    return (
      <div>
        {countries_id && (
          <>
            <ImageFlags
              countryCode={getCountryCodeById(countries_id)}
              className="d-inline-block mr-2"
            />
            <span>{company_name}</span>
          </>
        )}
        {!countries_id && (
          <div className="d-flex">
            <div className={styles['confidential-icon']}>
              <MuiIcon name="lock" style={{margin: 0, padding: 0}} />
            </div>
            <span>{company_name}</span>
          </div>
        )}
      </div>
    );
  }

  function renderSuggestionsContainer({containerProps, children}) {
    const {className, ...rest} = containerProps;

    let limitText = limitReached ? (
      <span>
        You have used up all your available credits. To view more companies
        upgrade <Link to="/membership/plans">here</Link>.
      </span>
    ) : (
      <span>
        You have used{' '}
        <strong>
          {getFormattedNumber(limitTry)}/{getFormattedNumber(limitMax)}
        </strong>{' '}
        of your verfications.
      </span>
    );
    const alertColor = limitReached ? 'danger' : 'primary';

    const limitAlert = (
      <div className="m-3">
        <Alert fade={false} color={alertColor}>
          <MuiIcon name={limitReached ? 'error' : 'info'} className="mr-2" />
          {limitText}
        </Alert>
      </div>
    );

    if (overallLoading) {
      return (
        <div
          className={cx(
            className,
            styles['single-search__suggestions-container--loading']
          )}
          {...rest}
        >
          <div className="d-flex align-items-center justify-content-center h-100">
            <p className="m-auto">Loading...</p>
          </div>
        </div>
      );
    }

    if (!hasSearched) {
      return (
        <div className={className} {...rest}>
          {displayLimit && limitAlert}
        </div>
      );
    }

    if (hasSearched && isEmpty(suggestions)) {
      return (
        <div className={className} {...rest}>
          <div
            className={cx(
              styles['single-search__suggestions-container--no-results'],
              'm-3 p-4'
            )}
          >
            <img
              src={UNABLE_TO_FIND_IMAGE_MATCHED}
              alt="try"
              className="mr-3"
            />
            <div>
              <h4 className="mb-0">Unable to find matched results</h4>
              <p className="mb-0">Can't find the company you're looking for?</p>

              <p className="mb-0">
                <Link to="/enquiry" className="text-primary font-weight-bold">
                  Send a request here
                </Link>
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (hasSearched && !isEmpty(suggestions)) {
      return (
        <div className={className} {...rest}>
          {children}

          <>
            <hr className="mt-2 mb-0" />
            <Button
              block
              outline
              color="white"
              className={cx(
                styles['single-search__suggestions-container-view-more-v2'],
                'p-2 text-primary d-flex justify-content-start'
              )}
              type="submit"
            >
              <span className="mx-2">
                {recordsCount} record(s) found, view more results here
              </span>
            </Button>
          </>

          {displayLimit && <hr className="mx-3" />}
          {displayLimit && limitAlert}

          {/* {limitReached && (
            <>
              <hr className="mt-2 mb-0" />
              <Button
                tag={Link}
                to={
                  isTrial
                    ? '/membership/plans/checkout?plan=basic&redirect=/advanced-search'
                    : '/membership/plans'
                }
                block
                outline
                color="white"
                className={cx(
                  styles['single-search__suggestions-container-view-more'],
                  'p-2 mb-0 text-danger'
                )}
              >
                <strong>Upgrade</strong> to search for more companies
              </Button>
            </>
          )} */}
          {/* If limit is not reached, show view more button */}
        </div>
      );
    }
  }

  const theme = themeGenerator(overallFocused, isMobile, false);

  const renderInputComponent = (inputProps) =>
    renderInputGenerator(
      inputProps,
      overallFocused,
      isMobile,
      header && {
        outline: true,
        color: 'white',
        children: <MuiIcon name="search" className="text-primary" size="md" />,
        className: 'p-1',
      }
    );

  const _inputProps = {
    ...inputProps,
    onFocus: async () => {
      setIsFocused(true);
      if (keyword) setHasSearched(true);

      await getLimit();
    },
  };

  function handleSuggestionSelected(event, {suggestion}) {
    event.preventDefault();

    const isCert = !!suggestion.certification_number;

    setHasSearched(false);
    setIsFocused(false);
    clearSuggestions();

    if (isLimitReached && isSubscribed && isEqual(storedOutOfCredits, '1')) {
      // enterprise
      if (
        subscriptionLevel > SUBSCRIPTION_TYPE_LEVEL.standard &&
        isManualMethod
      ) {
        alertByInsuffCredits({
          billingMethod,
          okLink: isTrial
            ? 'membership/plans/checkout?plan=basic'
            : 'membership/plans',
        });
        return;
      }

      // lower tiered plans
      toggleOutOfCreditsModal();
      return;
    } else if (isTrial && isLimitReached) {
      alertByInsuffCredits({
        billingMethod,
        okLink: isTrial
          ? 'membership/plans/checkout?plan=basic'
          : 'membership/plans',
      });
      return;
    }

    if (isCert) {
      const {certification_number} = suggestion;

      history.push({
        pathname: urlJoin(
          '/certification',
          removePrefix(certification_number, MDB_PREFIX.cert)
        ),
        state: {
          random: true,
          keyword: keyword,
        },
      });
    } else {
      const {company_id, company_name, countries_id} = suggestion;

      history.push({
        pathname: urlJoin(
          '/certified-entity',
          removePrefix(company_id, MDB_PREFIX.ce)
        ),
        state: {
          keyword: keyword,
          company_name: company_name,
          countries_id: countries_id,
        },
      });
    }
  }

  return (
    <Autosuggest
      theme={theme}
      alwaysRenderSuggestions
      suggestions={suggestions}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      onSuggestionSelected={handleSuggestionSelected}
      getSuggestionValue={getSuggestionValue}
      inputProps={_inputProps}
      renderSuggestion={renderSuggestion}
      renderSuggestionsContainer={renderSuggestionsContainer}
      renderInputComponent={renderInputComponent}
      {...rest}
    />
  );
}

export default withAdvancedSearchInput(AdvancedSearchInputV2);
